import { LogOut, Menu, Search, Settings, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback } from '@/Components/ui/Avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Components/ui/DropdownMenu';
import { IconButton } from '@/Components/ui/IconButton';
import GlobalSearch from './GlobalSearch';
import { cn } from '@/Components/ui/utils';

type HeaderUser = {
  first_name?: string | null;
  username?: string | null;
  email?: string | null;
  is_admin?: boolean;
  company_name?: string | null;
  company?: { social_name?: string; trade_name?: string } | null;
} | null;

type AppHeaderProps = {
  user: HeaderUser;
  onLogout: () => void;
  showMenuButton?: boolean;
  menuOpen?: boolean;
  onMenuClick?: () => void;
  topNav?: ReactNode;
};

function userInitials(nome: string | null | undefined): string {
  if (!nome?.trim()) return 'US';
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function displayName(user: HeaderUser): string {
  return user?.first_name?.trim() || user?.username?.trim() || 'Usuário';
}

export default function AppHeader({
  user,
  onLogout,
  showMenuButton = false,
  menuOpen = false,
  onMenuClick,
  topNav,
}: AppHeaderProps) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const name = displayName(user);
  const role = user?.is_admin ? 'Administrador' : 'Usuário';

  return (
    <>
      <header className={cn('nx-header', Boolean(topNav) && 'nx-header--top-nav nx-header--with-brand')}>
        {showMenuButton ? (
          <IconButton
            className="nx-menu-toggle"
            size="md"
            variant="ghost"
            label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
            aria-controls="nx-sidebar"
            onClick={onMenuClick}
          >
            {menuOpen ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
          </IconButton>
        ) : null}

        {topNav ? <>
          <div className="nx-header-brand"><img src="/logo_infor.png" alt="Infor" className="nx-brand-logo" width={28} height={28} /><span className="nx-header-brand-text">INDUSTRIALIZAÇÃO</span></div>
          <div className="nx-header-nav">{topNav}</div>
        </> : <button
          type="button"
          className="nx-search-trigger nx-global-search"
          onClick={() => setSearchOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={searchOpen}
          aria-label="Buscar no sistema"
        >
          <Search size={16} strokeWidth={2} aria-hidden />
          <span className="nx-search-trigger-label">Buscar no sistema…</span>
          <kbd>Ctrl + K</kbd>
        </button>}

        <div className="nx-header-actions">
          {topNav && <IconButton label="Buscar no sistema (Ctrl + K)" aria-haspopup="dialog" aria-expanded={searchOpen} onClick={() => setSearchOpen(true)}><Search size={17} /></IconButton>}
          <IconButton label="Configurações de layout" onClick={() => navigate('/layout')}><Settings size={17} /></IconButton>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="nx-user-trigger" aria-label="Menu do usuário">
                <Avatar>
                  <AvatarFallback>{userInitials(name)}</AvatarFallback>
                </Avatar>
                <span className="nx-user">
                  <span className="nx-user-name" title={name}>
                    {name}
                  </span>
                  <span className="nx-user-meta">{role}</span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8}>
              <DropdownMenuLabel>Conta conectada</DropdownMenuLabel>
              <div className="nexus-dropdown-meta">
                <strong>{name}</strong>
                <span>{role}</span>
                <span>{user?.company_name || user?.company?.trade_name || user?.company?.social_name || 'Empresa não informada'}</span>
                {user?.email ? <span>{user.email}</span> : null}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate('/layout')}>
                Preferências de layout
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate('/account-settings')}>
                <Settings size={15} strokeWidth={2} aria-hidden />
                Minha Conta
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="nx-dropdown-item--danger" onSelect={onLogout}>
                <LogOut size={15} strokeWidth={2} aria-hidden />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
